"use client";
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
const MultiColumnLayoutContext = React.createContext({
    leftSidebarWidth: "25%",
    rightSidebarWidth: "25%",
    isDesktop: false,
    hasLeftSidebar: false,
    hasRightSidebar: false,
});
// Main layout component
const ThreeColumnLayout = ({ children, leftSidebarWidth = "25%", rightSidebarWidth = "25%", debug = false, }) => {
    // Track window width to determine layout
    const [windowWidth, setWindowWidth] = useState(0);
    const [isDesktop, setIsDesktop] = useState(false);
    // Track which sidebars are present
    const [hasLeftSidebar, setHasLeftSidebar] = useState(false);
    const [hasRightSidebar, setHasRightSidebar] = useState(false);
    useEffect(() => {
        // Set initial width
        setWindowWidth(window.innerWidth);
        setIsDesktop(window.innerWidth >= 768);
        // Update width on resize
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setIsDesktop(window.innerWidth >= 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    // Check for sidebars in children
    useEffect(() => {
        let leftFound = false;
        let rightFound = false;
        React.Children.forEach(children, (child) => {
            if (React.isValidElement(child)) {
                if (child.type === LeftSidebar) {
                    leftFound = true;
                }
                else if (child.type === RightSidebar) {
                    rightFound = true;
                }
            }
        });
        setHasLeftSidebar(leftFound);
        setHasRightSidebar(rightFound);
    }, [children]);
    return (_jsxs(MultiColumnLayoutContext.Provider, { value: {
            leftSidebarWidth,
            rightSidebarWidth,
            isDesktop,
            hasLeftSidebar,
            hasRightSidebar,
        }, children: [debug && (_jsxs("div", { className: "fixed right-0 top-0 z-50 bg-black p-2 text-xs text-white", children: ["Width: ", windowWidth, "px | Layout: ", isDesktop ? "Desktop" : "Mobile", " | Left: ", hasLeftSidebar ? "Yes" : "No", " | Right:", " ", hasRightSidebar ? "Yes" : "No"] })), _jsx("div", { className: "min-h-screen w-full", style: {
                    display: "flex",
                    flexDirection: isDesktop ? "row" : "column",
                }, children: children })] }));
};
// Left Sidebar component
const LeftSidebar = ({ children }) => {
    const { isDesktop, leftSidebarWidth } = React.useContext(MultiColumnLayoutContext);
    return (_jsx("div", { style: {
            width: isDesktop ? leftSidebarWidth : "100%",
        }, children: _jsx("div", { style: {
                maxHeight: isDesktop ? "100vh" : "50vh",
            }, children: children }) }));
};
// Right Sidebar component
const RightSidebar = ({ children }) => {
    const { isDesktop, rightSidebarWidth } = React.useContext(MultiColumnLayoutContext);
    return (_jsx("div", { style: {
            width: isDesktop ? rightSidebarWidth : "100%",
            order: isDesktop ? 3 : 1, // Put at the end on desktop, beginning on mobile
        }, children: _jsx("div", { className: "overflow-y-auto", style: {
                maxHeight: isDesktop ? "100vh" : "50vh",
            }, children: children }) }));
};
// Main content component
const Content = ({ children }) => {
    const { isDesktop, hasLeftSidebar, hasRightSidebar, leftSidebarWidth, rightSidebarWidth, } = React.useContext(MultiColumnLayoutContext);
    // Calculate content width based on which sidebars are present
    const getContentWidth = () => {
        if (!isDesktop)
            return "100%";
        if (hasLeftSidebar && hasRightSidebar) {
            // Convert percentage strings to numbers for calculation
            const leftWidth = Number.parseFloat(leftSidebarWidth);
            const rightWidth = Number.parseFloat(rightSidebarWidth);
            return `${100 - leftWidth - rightWidth}%`;
        }
        else if (hasLeftSidebar) {
            const leftWidth = Number.parseFloat(leftSidebarWidth);
            return `${100 - leftWidth}%`;
        }
        else if (hasRightSidebar) {
            const rightWidth = Number.parseFloat(rightSidebarWidth);
            return `${100 - rightWidth}%`;
        }
        return "100%";
    };
    return (_jsx("div", { className: "bg-gray-50", style: {
            width: getContentWidth(),
            // TODO: Should WE apply PADDING or should we put that on ENGINEERS? ----> padding: isDesktop ? "2rem 0" : "1rem",
            order: 2, // Always in the middle
        }, children: _jsx("div", { 
            // bg-white shadow-sm
            className: "rounded-lg ", style: {
                // TODO: Should WE apply PADDING or should we put that on ENGINEERS? ----> padding: isDesktop ? "0 1.5rem" : "1rem",
                minHeight: isDesktop ? "calc(100vh - 4rem)" : "50vh",
            }, children: children }) }));
};
// Attach the sub-components to the main component
ThreeColumnLayout.LeftSidebar = LeftSidebar;
ThreeColumnLayout.RightSidebar = RightSidebar;
ThreeColumnLayout.Content = Content;
// For backward compatibility
ThreeColumnLayout.Sidebar = LeftSidebar;
export default ThreeColumnLayout;
